<template>
  <div class="signature-group">
    <div class="signature-container">
      <transition name="signature-fade" mode="out-in">
        <div v-if="hasSignature" :key="signatureUrl ?? undefined" class="signature-preview">
          <img :src="signatureUrl ?? undefined" :alt="label || 'Signature'" />
          <button v-if="canEdit" class="clear-signature-btn" @click="handleClear">
            {{ clearButtonText }}
          </button>
        </div>
        <div v-else-if="canEdit" class="signature-pad-container">
          <VueSignaturePad ref="signaturePad" :width="'100%'" :height="'200px'" :options="{
            penColor: 'black',
            backgroundColor: 'rgb(255, 255, 255)',
            velocityFilterWeight: 0.7,
            minWidth: 0.5,
            maxWidth: 2.5,
            throttle: 16,
            minDistance: 1
          }" />
          <div v-if="showActions" class="signature-actions">
            <button class="clear-btn" @click="clearPad">{{ clearPadText }}</button>
            <button class="save-btn" @click="handleSave" :disabled="saveDisabled">
              {{ saveButtonText }}
            </button>
          </div>
        </div>
      </transition>
    </div>
    <div v-if="error" class="error-message">{{ error }}</div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, watch } from 'vue'
  import { VueSignaturePad } from 'vue-signature-pad'

  const props = defineProps({
    modelValue: {
      type: String,
      default: null
    },
    canEdit: {
      type: Boolean,
      default: true
    },
    label: {
      type: String,
      default: 'Handtekening'
    },
    clearButtonText: {
      type: String,
      default: 'Wijzig handtekening'
    },
    clearPadText: {
      type: String,
      default: 'Wissen'
    },
    saveButtonText: {
      type: String,
      default: 'Opslaan'
    },
    saveDisabled: {
      type: Boolean,
      default: false
    },
    showActions: {
      type: Boolean,
      default: true
    },
    confirmClear: {
      type: Boolean,
      default: true
    },
    confirmMessage: {
      type: String,
      default: 'Weet u zeker dat u de handtekening wilt verwijderen?'
    },
    showError: {
      type: Boolean,
      default: true
    }
  })

  const emit = defineEmits(['update:modelValue', 'save', 'clear', 'error'])

  const signaturePad = ref<InstanceType<typeof VueSignaturePad> | null>(null)
  const error = ref('')
  const hasSignature = ref(false)

  // Watch for changes in modelValue
  watch(() => props.modelValue, (newValue) => {
    hasSignature.value = !!newValue
  }, { immediate: true })

  const signatureUrl = computed(() => {
    if (!props.modelValue) return null
    return `${props.modelValue}?t=${Date.now()}`
  })

  const clearPad = () => {
    if (signaturePad.value) {
      signaturePad.value.clearSignature()
      error.value = ''
    }
  }

  const handleClear = async () => {
    if (props.confirmClear && !confirm(props.confirmMessage)) {
      return
    }

    clearPad()
    hasSignature.value = false
    emit('clear')
    emit('update:modelValue', null)
  }

  const handleSave = async () => {
    if (!signaturePad.value) {
      const errorMsg = 'Signature pad not initialized'
      error.value = props.showError ? errorMsg : ''
      emit('error', errorMsg)
      return
    }

    const { isEmpty, data } = signaturePad.value.saveSignature()

    if (isEmpty) {
      const errorMsg = 'Please provide a signature'
      error.value = props.showError ? errorMsg : ''
      emit('error', errorMsg)
      return
    }

    error.value = ''
    hasSignature.value = true
    emit('update:modelValue', data)
    emit('save', data)
  }

  // Initialize hasSignature on mount
  onMounted(() => {
    hasSignature.value = !!props.modelValue
  })

  defineExpose({
    saveSignature: handleSave,
    clearSignature: clearPad
  })
</script>

<style scoped>
  .signature-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .signature-container {
    position: relative;
    width: 100%;
    min-height: 200px;
    border: 2px dashed #E5E7EB;
    border-radius: 8px;
    overflow: hidden;
    transition: all 0.3s ease;
  }

  .signature-container:hover {
    border-color: #9CA3AF;
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  .signature-preview {
    width: 100%;
    height: 100%;
    position: relative;
  }

  .signature-preview img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  /* Signature animations */
  .signature-fade-enter-active,
  .signature-fade-leave-active {
    transition: all 0.3s ease;
  }

  .signature-fade-enter-from,
  .signature-fade-leave-to {
    opacity: 0;
    transform: translateY(10px);
  }

  .signature-fade-enter-to,
  .signature-fade-leave-from {
    opacity: 1;
    transform: translateY(0);
  }

  .clear-signature-btn {
    position: absolute;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 8px 16px;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.2s;
  }

  .signature-preview:hover .clear-signature-btn {
    opacity: 1;
  }

  .signature-pad-container {
    width: 100%;
    height: 100%;
    min-height: 200px;
    display: flex;
    flex-direction: column;
  }

  .signature-actions {
    display: flex;
    gap: 8px;
    justify-content: center;
    padding: 8px;
    background: #f9fafb;
    border-top: 1px solid #e5e7eb;
  }

  .signature-actions button {
    padding: 6px 12px;
    border-radius: 4px;
    font-weight: 500;
    transition: all 0.2s;
  }

  .clear-btn {
    background: #f3f4f6;
    border: 1px solid #d1d5db;
    color: #374151;
  }

  .clear-btn:hover {
    background: #e5e7eb;
  }

  .save-btn {
    background: #2563eb;
    border: 1px solid #2563eb;
    color: white;
  }

  .save-btn:hover:not(:disabled) {
    background: #1d4ed8;
  }

  .save-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .error-message {
    color: #dc2626;
    font-size: 0.875rem;
    margin-top: 0.25rem;
  }
</style>
